/* KrakenTheMeta — card + deck helpers and Scryfall (no Firebase). Shared by the dashboard and the public pages.
 * Hangs off KD.data (the dashboard's data layer adds its Firestore functions to the same object).
 * The stored card shape (cardShape) is what deck-view, publishing and the Cloud Functions read: do not change it.
 */
import "./ui.js";

(function () {
  "use strict";

  var KD = window.KD;
  var D = (KD.data = KD.data || {});
  if (D.cardsReady) return;
  D.cardsReady = true;

  D.BASICS = new Set(["plains", "island", "swamp", "mountain", "forest", "wastes",
    "snow-covered plains", "snow-covered island", "snow-covered swamp", "snow-covered mountain", "snow-covered forest", "snow-covered wastes"]);
  D.chunked = function (arr, n) { var out = []; for (var i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n)); return out; };
  D.SCRY_HEADERS = { Accept: "application/json" };

  // Every Scryfall API call from the site goes through here (2026-10-05). Scryfall's hard limits are
  // 2 requests a second on /cards/search, /cards/named, /cards/collection and /cards/random (10 a second
  // elsewhere), and one 429 locks this browser out for 30 seconds — retrying inside that window only
  // extends it. Calls are spaced per endpoint, and during a lockout they answer 429 straight away.
  var SCRY_GAP = { search: 550, named: 550, collection: 550, random: 550, other: 110 };
  var scryNextAt = {}, scryLockedUntil = 0;
  D.scryfall = async function (url, opts) {
    var m = /api\.scryfall\.com\/cards\/(search|named|collection|random)\b/.exec(String(url));
    var lane = m ? m[1] : "other";
    var locked = function () { return new Response("{}", { status: 429, headers: { "Content-Type": "application/json" } }); };
    if (Date.now() < scryLockedUntil) return locked();
    var now = Date.now(), at = Math.max(now, scryNextAt[lane] || 0);
    scryNextAt[lane] = at + SCRY_GAP[lane];
    if (at > now) await KD.delay(at - now);
    if (Date.now() < scryLockedUntil) return locked();
    var res = await fetch(url, opts);
    if (res.status === 429) scryLockedUntil = Date.now() + 31000;
    return res;
  };

  // ================================================================ card data

  D.getCardImageUrl = function (card) {
    if (card.card_faces && card.card_faces.length > 0 && card.card_faces[0].image_uris) {
      var u = card.card_faces[0].image_uris; return u.normal || u.large || u.small;
    }
    if (card.image_uris) return card.image_uris.normal || card.image_uris.large || card.image_uris.small;
    if (card.id) return "https://api.scryfall.com/cards/" + card.id + "?format=image";
    return "";
  };

  // The stored card shape. deck-view, publishing and the functions read these exact fields.
  D.cardShape = function (cardData, quantity, isCommander) {
    return {
      name: cardData.name,
      imageUrl: D.getCardImageUrl(cardData),
      quantity: quantity || 1,
      price: (cardData.prices && cardData.prices.usd) || "N/A",
      types: cardData.type_line || "Unknown",
      manaCost: cardData.mana_cost || "",
      colors: cardData.colors || [],
      colorIdentity: cardData.color_identity || [],
      oracleText: cardData.oracle_text || "",
      power: cardData.power || "",
      toughness: cardData.toughness || "",
      setName: cardData.set_name || "Unknown Set",
      collectorNumber: cardData.collector_number || "N/A",
      producedMana: cardData.produced_mana || [],
      isCommander: !!isCommander
    };
  };
  D.huskCard = function (name, quantity, isCommander) {
    return { name: name, imageUrl: "", quantity: quantity, price: "N/A", types: "", manaCost: "", colors: [], colorIdentity: [], oracleText: "", power: "", toughness: "", setName: "", collectorNumber: "", producedMana: [], isCommander: !!isCommander };
  };
  // A Scryfall card, or a card already in the stored shape (a deck or collection row), as a new stored card.
  var SHAPE_KEYS = ["name", "imageUrl", "price", "types", "manaCost", "colors", "colorIdentity", "oracleText", "power", "toughness", "setName", "collectorNumber", "producedMana"];
  D.toShape = function (card, quantity, isCommander) {
    if (card.type_line !== undefined || card.image_uris || card.card_faces || card.prices) return D.cardShape(card, quantity, isCommander);
    var base = D.huskCard(card.name, quantity || 1, isCommander);
    SHAPE_KEYS.forEach(function (k) { if (card[k] !== undefined && card[k] !== null) base[k] = card[k]; });
    return base;
  };

  // Scryfall "normal" card image → just the illustration, for banners and tiles.
  D.artUrl = function (u) {
    if (!u) return u;
    if (u.indexOf("cards.scryfall.io/") !== -1) return u.replace(/\/(normal|large|small|png|border_crop)\//, "/art_crop/");
    if (u.indexOf("api.scryfall.com/cards/") !== -1) return u + (u.indexOf("?") !== -1 ? "&" : "?") + "version=art_crop";
    return u;
  };
  // commander > planeswalker > first real nonland spell > any card with an image
  D.pickHeaderCard = function (cards) {
    if (!Array.isArray(cards) || !cards.length) return null;
    var BASIC_NAMES = new Set(["plains", "island", "swamp", "mountain", "forest", "wastes"]);
    var c = cards.find(function (x) { return x.isCommander && x.imageUrl; }); if (c) return c.imageUrl;
    c = cards.find(function (x) { return /planeswalker/i.test(x.types || "") && x.imageUrl; }); if (c) return c.imageUrl;
    c = cards.find(function (x) { return x.imageUrl && !/land/i.test(x.types || "") && !BASIC_NAMES.has((x.name || "").toLowerCase()); }); if (c) return c.imageUrl;
    c = cards.find(function (x) { return x.imageUrl; });
    return c ? c.imageUrl : null;
  };

  // Brawl (MTG Arena) also lets any legendary planeswalker lead the deck.
  D.canBeCommander = function (card, format) {
    var type = String(card.types || card.type || "").split("//")[0].toLowerCase();
    var canBe = card.oracleText && String(card.oracleText).toLowerCase().indexOf("can be your commander") !== -1;
    if (type.indexOf("legendary") === -1) return false;
    if (type.indexOf("creature") !== -1) return true;
    if (type.indexOf("planeswalker") !== -1) return !!canBe || D.isBrawlFormat(format);
    return !!canBe;
  };
  D.nameKey = function (s) { return String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, " ").trim(); };
  D.namesMatchLoose = function (a, b) {
    if (!a || !b) return false;
    if (D.nameKey(a) === D.nameKey(b)) return true;
    return D.nameKey(String(a).split("//")[0]) === D.nameKey(String(b).split("//")[0]);
  };
  // A commander deck: singleton, built to the commander's colors. Brawl is MTG Arena's 100-card
  // version; Standard Brawl is its 60-card one.
  D.isCommanderFormat = function (format) { return !!format && ["commander", "edh", "1v1commander", "commander/edh", "brawl", "historicbrawl", "standardbrawl"].indexOf(String(format).toLowerCase()) !== -1; };
  D.isBrawlFormat = function (format) { return ["brawl", "historicbrawl", "standardbrawl"].indexOf(String(format || "").toLowerCase()) !== -1; };
  D.targetSize = function (format) { return String(format || "").toLowerCase() === "standardbrawl" ? 60 : D.isCommanderFormat(format) ? 100 : 60; };
  D.FORMATS = [["commander", "Commander"], ["brawl", "Brawl"], ["standard", "Standard"], ["modern", "Modern"], ["pioneer", "Pioneer"], ["legacy", "Legacy"], ["vintage", "Vintage"], ["pauper", "Pauper"], ["historic", "Historic"]];
  D.formatLabel = function (f) {
    f = String(f || "").toLowerCase();
    if (f === "edh" || f === "commander/edh") return "Commander";
    if (f === "standardbrawl") return "Standard Brawl";
    if (f === "historicbrawl") return "Brawl";
    var hit = D.FORMATS.find(function (x) { return x[0] === f; });
    return hit ? hit[1] : (f ? f.charAt(0).toUpperCase() + f.slice(1) : "Casual");
  };
  D.scryFormat = function (format) {
    var f = String(format || "").toLowerCase().replace(/[^a-z]/g, "");
    var map = { standard: "standard", pioneer: "pioneer", explorer: "explorer", modern: "modern", legacy: "legacy", vintage: "vintage", pauper: "pauper",
      commander: "commander", edh: "commander", brawl: "brawl", standardbrawl: "standardbrawl", historic: "historic", alchemy: "alchemy", timeless: "timeless", oathbreaker: "oathbreaker" };
    return map[f] || null;
  };

  D.categorize = function (cards) {
    var b = { Commander: [], Creatures: [], Planeswalkers: [], Instants: [], Sorceries: [], Artifacts: [], Enchantments: [], Battles: [], Lands: [], Other: [] };
    cards.forEach(function (c) {
      var t = String(c.types || "").toLowerCase();
      if (c.isCommander) b.Commander.push(c);
      else if (t.indexOf("creature") !== -1) b.Creatures.push(c);
      else if (t.indexOf("planeswalker") !== -1) b.Planeswalkers.push(c);
      else if (t.indexOf("instant") !== -1) b.Instants.push(c);
      else if (t.indexOf("sorcery") !== -1) b.Sorceries.push(c);
      else if (t.indexOf("battle") !== -1) b.Battles.push(c);
      else if (t.indexOf("land") !== -1) b.Lands.push(c);
      else if (t.indexOf("artifact") !== -1) b.Artifacts.push(c);
      else if (t.indexOf("enchantment") !== -1) b.Enchantments.push(c);
      else b.Other.push(c);
    });
    Object.keys(b).forEach(function (k) { b[k].sort(function (x, y) { return (x.name || "").localeCompare(y.name || ""); }); });
    return b;
  };

  // Mana value from a stored cost string ({2}{G}{G} → 4; X is 0; hybrid counts once; front face only).
  D.manaValue = function (cost) {
    var mv = 0, m, re = /\{([^}]+)\}/g, s = String(cost || "").split("//")[0];
    while ((m = re.exec(s))) {
      var sym = m[1];
      if (/^\d+$/.test(sym)) mv += parseInt(sym, 10);
      else if (/^[XYZ]$/.test(sym)) mv += 0;
      else if (/^2\//.test(sym)) mv += 2;
      else mv += 1;
    }
    return mv;
  };
  D.deckStats = function (cards) {
    var st = { total: 0, lands: 0, spells: 0, curve: [0, 0, 0, 0, 0, 0, 0, 0], colors: { W: 0, U: 0, B: 0, R: 0, G: 0, C: 0 }, mvSum: 0, price: 0, priced: 0 };
    cards.forEach(function (c) {
      var q = parseInt(c.quantity, 10) || 1;
      st.total += q;
      var p = parseFloat(c.price);
      if (!isNaN(p)) { st.price += p * q; st.priced += q; }
      var isLand = /land/i.test(c.types || "") && !/creature/i.test(c.types || "");
      if (isLand) { st.lands += q; return; }
      st.spells += q;
      var mv = D.manaValue(c.manaCost);
      st.mvSum += mv * q;
      st.curve[Math.min(7, mv)] += q;
      var cols = (c.colors && c.colors.length) ? c.colors : ["C"];
      cols.forEach(function (col) { if (st.colors[col] !== undefined) st.colors[col] += q; });
    });
    st.avgMv = st.spells ? st.mvSum / st.spells : 0;
    return st;
  };

  // ================================================================ Scryfall

  D.scrySearch = async function (q, sort, extra) {
    var url = "https://api.scryfall.com/cards/search?q=" + encodeURIComponent(q) + "&unique=" + ((extra && extra.unique) || "cards");
    if (sort) { var p = sort.split("-"); url += "&order=" + p[0] + (p[1] ? "&dir=" + p[1] : ""); }
    if (extra && extra.page) url += "&page=" + extra.page;
    var res = await D.scryfall(url, { headers: D.SCRY_HEADERS });
    if (res.status === 404) return { data: [], next: null, total: 0 };
    if (!res.ok) throw new Error("Scryfall " + res.status);
    var j = await res.json();
    return { data: j.data || [], next: j.has_more ? j.next_page : null, total: j.total_cards || (j.data || []).length };
  };
  D.scryNext = async function (url) {
    var res = await D.scryfall(url, { headers: D.SCRY_HEADERS });
    if (!res.ok) throw new Error("Scryfall " + res.status);
    var j = await res.json();
    return { data: j.data || [], next: j.has_more ? j.next_page : null };
  };

  // ONE parser for every bulk import (collection + deck): "4 Name", "4x Name", "SB: 2 Name", set and
  // foil annotations, Moxfield/Archidekt CSV, Arena section headers. `line` keeps the user's text.
  // 2026-10-03: entries carry `commander: true` for cards under a "Commander" header, tagged
  // "*CMDR*" (Moxfield) or "[Commander…]" (Archidekt) — imports used to drop the commander, so a
  // copied precon or a pasted list never knew who led it. CSV headers now cover ManaBox, Deckbox,
  // Dragon Shield, TCGplayer and Delver exports (comma, semicolon or tab), and MTGO .dek files read.
  D.parseListText = function (text) {
    var norm = (text || "").replace(/[‘’ʼ]/g, "'").replace(/[“”]/g, '"');
    var entries = [], skipped = [];
    // MTGO .dek (XML): <Cards CatID="..." Quantity="4" Sideboard="false" Name="Lightning Bolt" />
    if (/<\s*Cards\b[^>]*\bName\s*=/.test(norm)) {
      var re = /<\s*Cards\b([^>]*)\/?>/g, m0;
      while ((m0 = re.exec(norm))) {
        var attrs = m0[1];
        var nm0 = (attrs.match(/\bName\s*=\s*"([^"]*)"/) || [])[1];
        var q0 = parseInt((attrs.match(/\bQuantity\s*=\s*"(\d+)"/) || [])[1], 10);
        var sb = /\bSideboard\s*=\s*"true"/i.test(attrs);
        if (!nm0 || sb) { skipped.push(m0[0]); continue; }
        entries.push({ name: nm0.replace(/&amp;/g, "&").replace(/&apos;/g, "'").replace(/&quot;/g, '"').trim(), qty: q0 > 0 ? q0 : 1, line: m0[0] });
      }
      return { entries: entries, skipped: skipped };
    }
    var lines = norm.split(/\r?\n/).map(function (l) { return l.trim(); }).filter(Boolean);
    if (!lines.length) return { entries: entries, skipped: skipped };
    var head = lines[0];
    var delim = [",", ";", "\t"].map(function (d) { return [d, head.split(d).length - 1]; }).sort(function (a, b) { return b[1] - a[1]; })[0];
    var NAME_COLS = ["name", "card name", "card", "cardname", "card_name", "card title"];
    var QTY_COLS = ["count", "quantity", "qty", "amount", "copies", "number", "owned", "quantity owned", "have", "total quantity"];
    var splitRow = function (row) {
      var out = [], cur = "", inQ = false;
      for (var k = 0; k < row.length; k++) {
        var ch = row[k];
        if (ch === '"') { if (inQ && row[k + 1] === '"') { cur += '"'; k++; } else inQ = !inQ; }
        else if (ch === delim[0] && !inQ) { out.push(cur.trim()); cur = ""; }
        else cur += ch;
      }
      out.push(cur.trim());
      return out;
    };
    if (delim[1] > 0) {
      var header = splitRow(head).map(function (h) { return h.toLowerCase().replace(/^﻿/, "").trim(); });
      var nameCol = -1, qtyCol = -1;
      NAME_COLS.some(function (n) { nameCol = header.indexOf(n); return nameCol !== -1; });
      QTY_COLS.some(function (n) { qtyCol = header.indexOf(n); return qtyCol !== -1; });
      if (nameCol !== -1 && qtyCol !== -1) {
        for (var i = 1; i < lines.length; i++) {
          var cells = splitRow(lines[i]);
          var nm = (cells[nameCol] || "").trim();
          var q = parseInt(cells[qtyCol], 10);
          if (!nm || (isFinite(q) && q <= 0)) { skipped.push(lines[i]); continue; }
          entries.push({ name: nm, qty: isFinite(q) && q > 0 ? q : 1, line: lines[i] });
        }
        return { entries: entries, skipped: skipped };
      }
    }
    var HEADERS = new Set(["deck", "sideboard", "commander", "commanders", "companion", "maybeboard", "mainboard", "main deck", "tokens", "considering", "about"]);
    var prevWasAbout = false, inCommander = false, inSide = false;
    var SIDE = new Set(["sideboard", "maybeboard", "considering", "tokens"]);
    lines.forEach(function (line) {
      var bare = line.toLowerCase().replace(/:$/, "").replace(/\s*\(\d+\)$/, "");
      if (HEADERS.has(bare)) { skipped.push(line); prevWasAbout = bare === "about"; inCommander = bare === "commander" || bare === "commanders"; inSide = SIDE.has(bare); return; }
      if (prevWasAbout && /^name\s+/i.test(line)) { skipped.push(line); prevWasAbout = false; return; }
      prevWasAbout = false;
      var sbLine = /^sb:\s*/i.test(line);
      var m = line.replace(/^sb:\s*/i, "").match(/^(\d+)\s*[xX]?\s+(.+)$/);
      var qty, name;
      if (m) { qty = parseInt(m[1], 10); name = m[2]; } else { qty = 1; name = line; }
      var tagged = /\*cmdr\*|\[commander/i.test(name);
      // strip "(C21) 263", "[M21]", " *F*", " #123" — a bare trailing number with no # is part of
      // the card's name ("Black Waltz No. 3"), so it stays
      var prev;
      do {
        prev = name;
        name = name.replace(/\s*\((?:[A-Za-z0-9]{2,6})\)\s*[\w-]*\s*$/, "").replace(/\s*\[[^\]]*\]\s*$/, "").replace(/\s*\*[^*]*\*\s*$/, "").replace(/\s+#\d+\s*$/, "").trim();
      } while (name !== prev);
      if (!name || qty === 0) { skipped.push(line); return; }
      var e = { name: name, qty: qty || 1, line: line };
      if (inCommander || tagged) e.commander = true;
      if ((inSide || sbLine) && !e.commander) e.side = true; // sideboard / maybeboard: a deck import leaves these out
      entries.push(e);
    });
    return { entries: entries, skipped: skipped };
  };

  // Exact-name batches (/cards/collection, 70 per request, 3 attempts), then front faces for
  // two-face names, then fuzzy singles. Never throws; misses are the caller's to REPORT.
  D.resolveCardNames = async function (names, onProgress) {
    var want = Array.from(new Set(names.map(function (n) { return n.toLowerCase(); })));
    var out = new Map();
    var tick = function () { if (onProgress) onProgress(out.size, want.length); };
    async function batchResolve(pairs) {
      var groups = D.chunked(pairs, 70);
      for (var gi = 0; gi < groups.length; gi++) {
        var chunk = groups[gi];
        for (var attempt = 1; attempt <= 3; attempt++) {
          try {
            var res = await D.scryfall("https://api.scryfall.com/cards/collection", {
              method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify({ identifiers: chunk.map(function (p) { return { name: p.send }; }) })
            });
            if (res.ok) {
              var j = await res.json();
              var bySend = new Map();
              (j.data || []).forEach(function (cd) {
                if (!cd || !cd.name) return;
                bySend.set(cd.name.toLowerCase(), cd);
                var front = cd.name.split(" // ")[0].toLowerCase();
                if (!bySend.has(front)) bySend.set(front, cd);
              });
              chunk.forEach(function (p) { var cd = bySend.get(p.send.toLowerCase()); if (cd && !out.has(p.req)) out.set(p.req, cd); });
              break;
            }
          } catch (e) { /* retry */ }
          if (attempt < 3) await KD.delay(600 * attempt);
        }
        tick();
        if (gi < groups.length - 1) await KD.delay(120);
      }
    }
    await batchResolve(want.map(function (n) { return { req: n, send: n }; }));
    var twoFace = want.filter(function (n) { return !out.has(n) && n.indexOf("//") !== -1; })
      .map(function (n) { return { req: n, send: n.split("//")[0].trim() }; }).filter(function (p) { return p.send; });
    if (twoFace.length) await batchResolve(twoFace);
    var rest = want.filter(function (n) { return !out.has(n); });
    for (var i = 0; i < rest.length; i++) {
      var n = rest[i];
      await KD.delay(110);
      for (var a = 1; a <= 2; a++) {
        try {
          var r = await D.scryfall("https://api.scryfall.com/cards/named?fuzzy=" + encodeURIComponent(n));
          if (r.ok) { out.set(n, await r.json()); break; }
          if (r.status === 404) break;
        } catch (e) { /* retry */ }
        if (a < 2) await KD.delay(700);
      }
      tick();
    }
    return { resolved: out, unmatched: want.filter(function (x) { return !out.has(x); }) };
  };
})();
